// deps
import React, { ReactElement, useCallback, useMemo } from "react";

import {
    useEffectOnce,
    ToggleSwitch,
    handleKeyboardActivation,
    useIsomorphicLayoutEffect,
} from "@swiggy-private/core-ui";

// constants
import { ACCESSIBILITY_ROLES } from "@payment-ui/constants";

// interface
import { B2C_BALANCE_ELIGIBILITY, BillToCompanyWidgetData } from "@swiggy-private/checkout-types";

// dls
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// styles
import { B2CContainer, B2CWrapper, IconContainer, ContentWrapper, Icon } from "./styles";

// js-utils
import { format, isEmpty } from "@swiggy-private/js-utils";

// constant
import { B2C_TEXTS, B2C_WIDGET_ACCESSIBILITY_ID } from "./constant";

// test-ids
import { B2C_WIDGET_TEST_IDS } from "@swiggy-private/payment-test-ids";

// A payment refetch unmounts this widget for a render, so the focus intent has to outlive the
// instance that recorded it. Per-instance refs are reset on remount and lose the restore.
let shouldRestoreB2cWidgetFocus = false;
let b2cToggleRefetchPending = false;
let b2cToggleRefetchSeenLoading = false;
let b2cWrapperHadFocus = false;

export interface BillToCompanyWidgetProps {
    onToggle: VoidFunction;
    data: BillToCompanyWidgetData;
    isSelected: boolean;
    onLoad?: VoidFunction;
    isPaymentRefetching?: boolean;
}

function BillToCompanyWidget(props: BillToCompanyWidgetProps): ReactElement | null {
    const { onToggle, data, isSelected, onLoad, isPaymentRefetching = false } = props;
    const { title, enabled, icon, balance, disableMessage, balanceEligibility } = data;

    useEffectOnce(() => {
        onLoad && onLoad();
    });

    const attemptFocusRestore = useCallback(
        (node?: HTMLElement | null) => {
            const wrapper = node ?? document?.getElementById(B2C_WIDGET_ACCESSIBILITY_ID);
            if (!wrapper) {
                return;
            }

            wrapper.focus({ preventScroll: true });
            if (document.activeElement !== wrapper) {
                return;
            }

            if (!shouldRestoreB2cWidgetFocus) {
                return;
            }

            if (b2cToggleRefetchPending) {
                if (!b2cToggleRefetchSeenLoading || isPaymentRefetching) {
                    return;
                }
                shouldRestoreB2cWidgetFocus = false;
                b2cToggleRefetchPending = false;
                b2cToggleRefetchSeenLoading = false;
                return;
            }

            shouldRestoreB2cWidgetFocus = false;
        },
        [isPaymentRefetching],
    );

    const wrapperRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (!node) {
                return;
            }

            if (shouldRestoreB2cWidgetFocus || b2cWrapperHadFocus) {
                attemptFocusRestore(node);
            }
            if (b2cWrapperHadFocus && !shouldRestoreB2cWidgetFocus) {
                b2cWrapperHadFocus = false;
            }
        },
        [attemptFocusRestore],
    );

    const onToggleClick = useCallback(() => {
        if (!data.enabled) {
            return;
        }
        shouldRestoreB2cWidgetFocus = true;
        b2cToggleRefetchPending = true;
        b2cToggleRefetchSeenLoading = false;
        onToggle();
        attemptFocusRestore();
    }, [onToggle, data, attemptFocusRestore]);

    useIsomorphicLayoutEffect(() => {
        if (shouldRestoreB2cWidgetFocus || b2cWrapperHadFocus) {
            if (isPaymentRefetching) {
                b2cToggleRefetchSeenLoading = true;
            }
            attemptFocusRestore();
            if (b2cWrapperHadFocus && !shouldRestoreB2cWidgetFocus) {
                b2cWrapperHadFocus = false;
            }
        }
    }, [isSelected, isPaymentRefetching, attemptFocusRestore]);

    useIsomorphicLayoutEffect(() => {
        return () => {
            if (document.activeElement?.id === B2C_WIDGET_ACCESSIBILITY_ID) {
                b2cWrapperHadFocus = true;
            }
        };
    });

    const balanceText = useMemo(() => {
        if (isEmpty(balance) || balanceEligibility === B2C_BALANCE_ELIGIBILITY.UNLIMITED) {
            return "";
        }
        if (balanceEligibility === B2C_BALANCE_ELIGIBILITY.LOW_BALANCE) {
            return format(B2C_TEXTS.LOW_BALANCE_TEXT, balance);
        }
        return format(B2C_TEXTS.BALANCE_TEXT, balance);
    }, [balanceEligibility, balance]);

    // Keep the accessible name aligned with the visible sub-text (see renderSubText):
    // disable message when disabled, balance only when selected, otherwise just the title.
    const accessibilityLabel = useMemo(() => {
        let subText = "";
        if (!isEmpty(disableMessage) && !enabled) {
            subText = disableMessage;
        } else if (balanceEligibility !== B2C_BALANCE_ELIGIBILITY.UNLIMITED && !isEmpty(balance) && isSelected) {
            subText = balanceText;
        }
        return [title, subText].filter(Boolean).join(", ");
    }, [title, disableMessage, enabled, balanceEligibility, balance, isSelected, balanceText]);

    const renderSubText = useCallback(() => {
        if (!isEmpty(disableMessage) && !enabled) {
            return (
                <Text
                    variant="V2.body3.neutral.regular"
                    color="text_Low_Emphasis"
                    data-testid={B2C_WIDGET_TEST_IDS.DISABLE_MESSAGE}
                >
                    {disableMessage}
                </Text>
            );
        }
        if (balanceEligibility === B2C_BALANCE_ELIGIBILITY.UNLIMITED) {
            return null;
        }
        if (!isEmpty(balance) && isSelected) {
            return (
                <Text
                    variant="V2.body3.neutral.regular"
                    color={
                        balanceEligibility === B2C_BALANCE_ELIGIBILITY.LOW_BALANCE ? "IG_Primary" : "text_Low_Emphasis"
                    }
                    data-testid={B2C_WIDGET_TEST_IDS.BALANCE_TEXT}
                >
                    {balanceText}
                </Text>
            );
        }
        return null;
    }, [disableMessage, balanceText, balanceEligibility, isSelected]);

    return (
        <B2CContainer data-testid={B2C_WIDGET_TEST_IDS.CONTAINER}>
            <B2CWrapper
                ref={wrapperRef}
                id={B2C_WIDGET_ACCESSIBILITY_ID}
                data-testid={B2C_WIDGET_TEST_IDS.WRAPPER}
                role={ACCESSIBILITY_ROLES.CHECKBOX}
                aria-checked={isSelected}
                aria-disabled={!enabled}
                aria-label={accessibilityLabel}
                tabIndex={enabled ? 0 : -1}
                onClick={onToggleClick}
                onKeyDown={handleKeyboardActivation(onToggleClick)}
            >
                {!isEmpty(icon) && (
                    <IconContainer data-testid={B2C_WIDGET_TEST_IDS.ICON_CONTAINER} aria-hidden="true">
                        <Icon cloudinaryId={icon} fit="contain" />
                    </IconContainer>
                )}
                <ContentWrapper data-testid={B2C_WIDGET_TEST_IDS.CONTENT_WRAPPER}>
                    <Text
                        variant="V2.body2.neutral.bold"
                        color={enabled ? "text_High_Emphasis" : "text_disabled"}
                        data-testid={B2C_WIDGET_TEST_IDS.TITLE}
                    >
                        {title}
                    </Text>
                    {renderSubText()}
                </ContentWrapper>
                <ToggleSwitch
                    selected={isSelected}
                    isDisabled={!data.enabled}
                    backgroundColor="background_Overlay_Light"
                    data-testid={B2C_WIDGET_TEST_IDS.TOGGLE_SWITCH}
                />
            </B2CWrapper>
        </B2CContainer>
    );
}

export default React.memo(BillToCompanyWidget);
